<template>
  <div class="min-h-screen w-full flex items-center justify-center bg-[#f0f3f8] p-4 sm:p-8 font-sans antialiased text-slate-800">
    
    <!-- Carte principale conteneur -->
    <div class="w-full max-w-5xl bg-white rounded-3xl shadow-2xl shadow-slate-200/60 overflow-hidden flex flex-col md:flex-row border border-slate-100">
      
      <!-- SECTION GAUCHE (50%) : Photo + Panneau Bleu royal -->
      <div class="w-full md:w-1/2 flex flex-col">
        
        <!-- Haut Gauche : Photo du Médecin -->
        <div class="h-64 sm:h-72 md:h-1/2 relative bg-slate-100 overflow-hidden">
          <img 
            src="../assets/medical-equipment-with-copy-space.jpg" 
            alt="Cabinet Dr. Thierry Paradis" 
            class="w-full h-full object-cover object-center"
          />
        </div>

        <!-- Bas Gauche : Bloc Bleu Vif avec Message de Bienvenue -->
        <div class="flex-1 bg-[#3b59e9] p-8 sm:p-10 flex flex-col justify-between text-white relative">
          
          <!-- Logo discret dans le bloc bleu -->
          <div class="flex items-center gap-2.5 font-bold text-lg tracking-tight mb-8">
            <div class="w-7 h-7 rounded-full border-2 border-white flex items-center justify-center">
              <div class="w-2.5 h-2.5 bg-white rounded-full"></div>
            </div>
            <span>Cabinet Indépendant</span>
          </div>

          <!-- Encadré de bienvenue avec barre latérale blanche -->
          <div class="pl-5 border-l-2 border-white/80 my-auto">
            <p class="text-xs font-medium text-white/80 uppercase tracking-wider mb-1">Espace Praticien</p>
            <h2 class="text-2xl sm:text-3xl font-bold text-white tracking-tight leading-tight mb-2">
              Dr. Thierry Paradis
            </h2>
            <p class="text-xs text-white/80 font-normal leading-relaxed">
              Logiciel de gestion de cabinet indépendant, sécurisé et centralisé pour vos patients.
            </p>
          </div>

        </div>
      </div>

      <!-- SECTION DROITE (50%) : Formulaire de connexion -->
      <div class="w-full md:w-1/2 p-8 sm:p-12 md:p-16 flex flex-col justify-between bg-white">
        
        <!-- Logo / Titre Cabinet -->
        <div class="flex items-center gap-2.5 font-bold text-xl text-[#3b59e9] tracking-tight mb-8">
          <div class="w-8 h-8 rounded-full bg-[#3b59e9] text-white flex items-center justify-center shadow-md shadow-blue-500/20">
            <div class="w-3 h-3 border-2 border-white rounded-full"></div>
          </div>
          <span class="text-slate-900 text-sm font-extrabold uppercase tracking-wider">Dr. Thierry Paradis</span>
        </div>

        <!-- Contenu du Formulaire -->
        <div class="my-auto">
          <h1 class="text-2xl font-extrabold text-slate-900 tracking-tight mb-1">Connexion</h1>
          <p class="text-xs text-slate-400 font-medium mb-8">Saisissez vos identifiants pour accéder à votre espace</p>

          <!-- Banner de Message d'Erreur -->
          <Transition name="fade">
            <div 
              v-if="errorMessage" 
              class="mb-6 p-3.5 rounded-xl bg-rose-50 border border-rose-100 text-rose-600 text-xs font-semibold flex items-center gap-2.5"
            >
              <svg class="w-4 h-4 flex-shrink-0 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{{ errorMessage }}</span>
            </div>
          </Transition>

          <form @submit.prevent="handleLogin" class="space-y-5">
            
            <!-- Champ Email / Identifiant -->
            <div>
              <label class="block text-xs font-bold text-slate-700 mb-1.5" for="username">
                Identifiant / Email
              </label>
              <input 
                id="username" 
                v-model="username" 
                type="text" 
                required
                autocomplete="username"
                placeholder="ex: dr.paradis" 
                class="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder-slate-300 text-xs font-medium focus:outline-none focus:border-[#3b59e9] focus:ring-4 focus:ring-blue-500/10 transition-all"
              />
            </div>

            <!-- Champ Mot de passe -->
            <div>
              <label class="block text-xs font-bold text-slate-700 mb-1.5" for="password">
                Mot de passe
              </label>
              <div class="relative">
                <input 
                  id="password" 
                  v-model="password" 
                  :type="showPassword ? 'text' : 'password'" 
                  required
                  autocomplete="current-password"
                  placeholder="••••••••••••••••" 
                  class="w-full pl-4 pr-10 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder-slate-300 text-xs font-medium focus:outline-none focus:border-[#3b59e9] focus:ring-4 focus:ring-blue-500/10 transition-all"
                />
                <button 
                  type="button" 
                  @click="showPassword = !showPassword"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 transition-colors"
                  tabindex="-1"
                >
                  <svg v-if="!showPassword" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858-5.908a10.025 10.025 0 0110.123 7c-.571 1.822-1.637 3.428-3.029 4.618M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3l18 18" />
                  </svg>
                </button>
              </div>
              <div class="text-right mt-2">
                <a href="#" class="text-[11px] font-bold text-[#3b59e9] hover:underline">Mot de passe oublié ?</a>
              </div>
            </div>

            <!-- Bouton Se Connecter -->
            <button 
              type="submit" 
              :disabled="isLoading"
              class="w-full py-3.5 px-6 rounded-xl font-bold text-xs text-white bg-[#3b59e9] hover:bg-blue-700 active:bg-blue-800 transition-all duration-200 shadow-md shadow-blue-500/20 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
            >
              <span v-if="!isLoading">Se Connecter</span>
              <span v-else class="flex items-center justify-center gap-2">
                <svg class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Connexion...
              </span>
            </button>
          </form>
        </div>

        <!-- Bas de page -->
        <div class="text-center pt-6 text-[11px] text-slate-400 font-medium">
          Cabinet Médical Indépendant — Dr. Thierry Paradis
        </div>

      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'LoginView',
  data() {
    return {
      username: '',
      password: '',
      showPassword: false,
      isLoading: false,
      errorMessage: ''
    };
  },
  methods: {
    async handleLogin() {
      this.errorMessage = '';
      this.isLoading = true;

      try {
        const response = await axios.post('https://thierry-paradis-medecin-743b8e1c5de0.herokuapp.com/login', {
          username: this.username,
          password: this.password
        });

        if (response.status === 200 && response.data.token) {
          localStorage.setItem('auth_token', response.data.token);
          this.$router.push('/');
        } else {
          this.errorMessage = 'Identifiants invalides.';
        }
      } catch (error) {
        if (error.response && error.response.data && error.response.data.message) {
          this.errorMessage = error.response.data.message;
        } else {
          this.errorMessage = 'Impossible de se connecter au serveur. Veuillez réessayer.';
        }
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>